import * as React from 'react';

import classnames from 'classnames';

import {keyWithIds} from 'nutshell-core/schema';

import type {SelectMenuOption} from '../../action-menu/action-menu';
import {Card} from '../../card/card';
import {colors} from '../../colors/colors';
import {formatValue} from '../../entity/entity-display-values/helpers';
// @ts-expect-error - imported from .js/.jsx file
import {LoadingIcon} from '../../icon/svg/loading-icon';
import {StackLegacy} from '../../layout/stack/stack-legacy';

import {getValue} from './helpers';
import {LeadCardHeader} from './lead-card-header';
import {LeadCardRow} from './lead-card-row';

import styles from './lead-card-dynamic.module.css';

type Props = {
    listItem: any;
    linkable: boolean;
    hasActionMenu?: boolean;
    isDraggable?: boolean;
    isDragging?: boolean;
    options?: SelectMenuOption[];
    onActionMenuOpen?: (leadId: string) => any;
    borderColor?: string;
    onNavigate?: () => void;
    isLoading?: boolean;
    fieldsToDisplay: string[];
    isLoadingFields?: boolean;
    schema: any;
    anchorElementId?: string;
};

export function LeadCardDynamic(props: Props) {
    const {
        hasActionMenu = true,
        listItem,
        schema,
        fieldsToDisplay,
        isDragging,
        isDraggable,
        borderColor,
    } = props;

    const containerStyleNames = classnames(styles.container, {
        [styles['container--hoverable']]: !isDragging && isDraggable,
    });

    const schemaKeyedWithIds = React.useMemo(() => {
        return keyWithIds(schema);
    }, [schema]);

    const cardFields = React.useMemo(() => {
        return fieldsToDisplay
            .map((fieldId) => {
                const fieldKey = schemaKeyedWithIds[fieldId]
                    ? schemaKeyedWithIds[fieldId].name
                    : fieldId;

                const schemaField = schemaKeyedWithIds ? schemaKeyedWithIds[fieldId] : undefined;
                const rowType = schemaField && (schemaField.cellType || schemaField.type);

                // Handle async fields - they don't need a value, they fetch their own data
                if (rowType === 'async') {
                    const entityId =
                        listItem.links && listItem.links.entity && listItem.links.entity.id;
                    if (!entityId) {
                        return null;
                    }

                    return (
                        <LeadCardRow
                            key={fieldId}
                            schemaField={schemaField}
                            value=''
                            isAsync={true}
                            asyncFieldName={fieldKey}
                            entityId={entityId}
                        />
                    );
                }

                const value = getValue(fieldKey, listItem, true);
                const formattedValue = getFormattedValue(value, schemaField, fieldKey);

                if (!formattedValue) {
                    return null;
                }

                return (
                    <LeadCardRow key={fieldId} schemaField={schemaField} value={formattedValue} />
                );
            })
            .filter((field) => field);
    }, [fieldsToDisplay, listItem, schemaKeyedWithIds]);

    return (
        <Card hasBorder={Boolean(borderColor)} borderColor={borderColor}>
            {props.isLoading ? (
                <div className='pad-16'>
                    <LoadingIcon size={24} fill={colors.offWhiteDk} />
                </div>
            ) : (
                <div className={containerStyleNames}>
                    <LeadCardHeader
                        id={listItem.links.entity.id}
                        leadName={listItem.primaryName}
                        leadStatus={
                            listItem.fields.status ? listItem.fields.status.value.value : ''
                        }
                        avatarUrl={listItem.avatarUrl}
                        htmlUrlPath={listItem.htmlUrlPath}
                        priority={listItem.priority}
                        linkable={props.linkable}
                        value={listItem.fields.value.formattedValue}
                        hasActionMenu={hasActionMenu}
                        options={props.options}
                        onActionMenuOpen={props.onActionMenuOpen}
                        onNavigate={props.onNavigate}
                        anchorElementId={props.anchorElementId}
                    />
                    {/* This prevents us from showing a loading state while the card is dragging */}
                    {!props.fieldsToDisplay.length && props.isLoadingFields ? (
                        <div className={styles.loading}>
                            <LoadingIcon size={24} fill={colors.offWhiteDk} />
                        </div>
                    ) : undefined}
                    {cardFields.length ? (
                        <div className={styles.fields}>
                            <StackLegacy spacing={4}>{cardFields}</StackLegacy>
                        </div>
                    ) : undefined}
                </div>
            )}
        </Card>
    );
}

function getFormattedValue(
    value: any,
    schemaField: any,
    fieldKey: string
): string | number | any[] | null | undefined {
    // Async fields don't need formatted values - they fetch their own data
    const rowType = schemaField && (schemaField.cellType || schemaField.type);
    if (rowType === 'async') {
        return null;
    }

    const formattedValue = schemaField ? formatValue(value, schemaField.type, fieldKey) : undefined;

    if (!formattedValue) {
        return null;
    }

    if (typeof formattedValue === 'object' && !Array.isArray(formattedValue)) {
        // Some of our formatted values are objects so we can show multiple pieces of information in a list context.
        // For our board view, we only need the formatted value (not the raw value)
        if (typeof formattedValue.value === 'string') {
            return formattedValue.value;
        }
    } else {
        return formattedValue;
    }
}
